import type { ReactElement } from 'react';
import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import useAsyncEffect from 'hooks/useAsyncEffect';
import useCurrentUser from 'hooks/useCurrentUser';

import WelcomeSurveyModal from 'components/shared/modals/WelcomeSurveyModal';
import { FeatureFlag } from 'config';
import Header from '../Header';
import MobileNav from '../MobileNav';
import Sidebar from '../Sidebar';
import Footer from '../Footer';
import SidebarNavigation from '../../MyAccount/SidebarNavigation';
import MyAccountLayout from '../../MyAccount/MyAccountLayout';
import { CookieModal } from '../../../../platform/components/shared/modal/CookieModal';

type Props = {
  hasSidebar?: boolean;
  isAccountPage?: boolean;
};

function LoggedIn({
  hasSidebar = true,
  isAccountPage = false,
}: Props): ReactElement {
  const [isMobileNavActive, setMobileNavActive] = useState(false);

  const { currentUser } = useCurrentUser();
  const isAccountManagementEnabled = currentUser.hasFeatureEnabled(FeatureFlag.ACCOUNT_MANAGEMENT);
  const isCookieBannerEnabled = window.gon?.featureFlags?.isCookieBannerEnabled;

  // Set 'showWelcomeSurvey' localStorage item to the date and time you want
  // the welcome survey modal to show. If you want it to show immediately, just
  // set it to the current or a past time. It supports any date format that
  // can be parsed by JS Date class, like UTC string or Epoch milliseconds.
  const showWelcomeSurvey = localStorage.getItem('showWelcomeSurvey');
  const [showSurveyModal, setShowSurveyModal] = useState(false);

  useAsyncEffect(async (isMounted) => {
    setTimeout(() => {
      if (isMounted.current) {
        setShowSurveyModal(
          // @ts-expect-error TODO: fix null safety
          showWelcomeSurvey && new Date(showWelcomeSurvey) <= new Date(),
        );
      }
    }, 2000);
  }, []);

  const toggleMobileNav = () => setMobileNavActive(!isMobileNavActive);

  return (
    <>
      <a className="sr-only sr-only-focusable" href="#content">
        Skip to main content
      </a>

      <Header
        isMobileNavActive={isMobileNavActive}
        onMobileNavClick={toggleMobileNav}
      />

      <MobileNav
        isActive={isMobileNavActive}
        onClose={toggleMobileNav}
      />

      <main id="content" aria-label="Main content">
        {hasSidebar ? (
          <div className="l-container">
            <div className="row justify-content-center">
              {isAccountManagementEnabled && isAccountPage ? (
                <div style={{
                  display: 'flex',
                  width: '100%',
                }}
                >
                  <div className="d-none d-lg-block c-my-account__sidebar">
                    <SidebarNavigation />
                  </div>
                  <MyAccountLayout />
                </div>
              ) : (
                <>
                  {!isAccountManagementEnabled && (
                    <div className="d-none d-lg-block col-lg-2">
                      <Sidebar />
                    </div>
                  )}
                  <Outlet />
                </>
              )}
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {showSurveyModal && (
        <WelcomeSurveyModal onClose={() => setShowSurveyModal(false)} />
      )}

      <Footer />
      {isCookieBannerEnabled && <CookieModal />}
    </>
  );
}

export default LoggedIn;
